// src/pages/qbo/SnapshotsCard.tsx
//
// Before / after pictures of the QuickBooks company around a conversion
// batch (agent 3.18.7). A snapshot records the placeholder A/R customer
// balance, every customer balance, the A/R account total and the monthly
// P&L. Comparing two of them answers the three questions nobody wants to
// check by hand after each batch: did the placeholder fall by exactly what
// the named customers gained, is total A/R unchanged, is the P&L unchanged
// in every month.

import { useEffect, useState } from 'react';
import { Camera, CheckCircle2, XCircle } from 'lucide-react';
import qboApi from '../../services/qboApi';
import type { SnapshotCompare, SnapshotSummary } from './qboTypes';
import { money } from './qboTypes';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
}

const when = (iso: string) => (iso ? new Date(iso).toLocaleString() : '');
const signed = (v: number) => `${v > 0 ? '+' : ''}${money(v)}`;

export default function SnapshotsCard({ onMessage }: Props) {
  const [list, setList] = useState<SnapshotSummary[]>([]);
  const [label, setLabel] = useState('');
  const [taking, setTaking] = useState(false);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [cmp, setCmp] = useState<SnapshotCompare | null>(null);
  const [comparing, setComparing] = useState(false);
  const [supported, setSupported] = useState(true);

  const load = async () => {
    try {
      const l = (await qboApi.conversionSnapshots()) as SnapshotSummary[];
      setList(Array.isArray(l) ? l : []);
      setSupported(true);
    } catch {
      setSupported(false);   // agent older than 3.18.7
    }
  };
  useEffect(() => { load(); }, []);

  const take = async () => {
    setTaking(true);
    try {
      const s = (await qboApi.conversionSnapshot(label)) as SnapshotSummary;
      onMessage('success', `Snapshot ${s.id} taken`);
      setLabel('');
      await load();
    } catch (e: unknown) {
      onMessage('error', `Snapshot: ${(e as Error).message}`);
    } finally {
      setTaking(false);
    }
  };

  const compare = async () => {
    if (!a || !b) return;
    setComparing(true);
    try {
      setCmp((await qboApi.conversionCompare(a, b)) as SnapshotCompare);
    } catch (e: unknown) {
      onMessage('error', `Compare: ${(e as Error).message}`);
    } finally {
      setComparing(false);
    }
  };

  if (!supported) return null;

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-400 flex items-center gap-2"><Camera size={14} /> Before / after snapshots</div>
          <div className="text-[11px] text-slate-500">Take one before a conversion batch and one after, then compare. Expected: placeholder A/R down by the batch total, named customers up by the same, total A/R and every month's P&amp;L unchanged.</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 w-40"
            placeholder="label (e.g. before 9/25)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50" onClick={take} disabled={taking}>
            {taking ? 'Taking…' : 'Take snapshot'}
          </button>
        </div>
      </div>

      {list.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-700/60 mb-3">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">A</th>
                <th className="px-2 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">B</th>
                <th className="text-left px-3 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">Taken</th>
                <th className="text-left px-3 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">Label</th>
                <th className="text-right px-3 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">Placeholder A/R</th>
                <th className="text-right px-3 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">Total A/R</th>
                <th className="text-right px-3 py-1.5 border-b border-slate-700 text-slate-500 font-semibold text-[10px] uppercase">Customers with balance</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="hover:bg-slate-700/30">
                  <td className="px-2 py-1 border-b border-slate-800/60 text-center"><input type="radio" name="snapA" checked={a === s.id} onChange={() => setA(s.id)} /></td>
                  <td className="px-2 py-1 border-b border-slate-800/60 text-center"><input type="radio" name="snapB" checked={b === s.id} onChange={() => setB(s.id)} /></td>
                  <td className="px-3 py-1 border-b border-slate-800/60 text-slate-300 whitespace-nowrap" title={s.id}>{when(s.takenAt)}</td>
                  <td className="px-3 py-1 border-b border-slate-800/60 text-slate-200">{s.label || <span className="text-slate-600">—</span>}{s.warnings?.length ? <span className="ml-1 text-amber-300" title={s.warnings.join('\n')}>⚠</span> : null}</td>
                  <td className="px-3 py-1 border-b border-slate-800/60 text-right font-mono text-slate-200">${money(s.placeholderBalance)}</td>
                  <td className="px-3 py-1 border-b border-slate-800/60 text-right font-mono text-slate-200">${money(s.arTotal)}</td>
                  <td className="px-3 py-1 border-b border-slate-800/60 text-right text-slate-400">{s.customersWithBalance} / {s.customersTotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.length === 0 && <div className="text-xs text-slate-500 mb-2">No snapshots yet.</div>}

      <div className="flex items-center gap-3">
        <button className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-slate-600 hover:bg-slate-500 disabled:opacity-50" onClick={compare} disabled={!a || !b || a === b || comparing}>
          {comparing ? 'Comparing…' : 'Compare A → B'}
        </button>
        {a && b && a === b && <span className="text-[11px] text-slate-500">pick two different snapshots</span>}
      </div>

      {cmp && (
        <div className="mt-4 space-y-3">
          <ul className="space-y-1">
            {cmp.checks.map((c) => (
              <li key={c.name} className={`flex items-start gap-2 text-xs ${c.ok ? 'text-green-300' : 'text-red-300'}`}>
                {c.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
                <span><span className="font-medium">{c.name}</span> <span className="text-slate-400">— {c.detail}</span></span>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Placeholder A/R</div><div className="text-sm font-mono text-white">${money(cmp.placeholder.before)} → ${money(cmp.placeholder.after)}</div><div className={`text-[11px] font-mono ${cmp.placeholder.delta < 0 ? 'text-green-400' : cmp.placeholder.delta > 0 ? 'text-amber-300' : 'text-slate-500'}`}>{signed(cmp.placeholder.delta)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Named customers</div><div className="text-sm font-mono text-white">{cmp.customers.changedCount} changed</div><div className={`text-[11px] font-mono ${cmp.customers.deltaSum > 0 ? 'text-green-400' : cmp.customers.deltaSum < 0 ? 'text-amber-300' : 'text-slate-500'}`}>{signed(cmp.customers.deltaSum)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Total A/R</div><div className="text-sm font-mono text-white">${money(cmp.arTotal.before)} → ${money(cmp.arTotal.after)}</div><div className={`text-[11px] font-mono ${Math.abs(cmp.arTotal.delta) < 0.01 ? 'text-slate-500' : 'text-red-300'}`}>{signed(cmp.arTotal.delta)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">P&amp;L cells moved</div><div className="text-sm font-mono text-white">{cmp.pnl.changedCount}</div><div className="text-[11px] text-slate-500">of {cmp.pnl.columnsCompared} months × 6 rows</div></div>
          </div>

          {cmp.customers.changed.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-400">Customers whose balance changed ({cmp.customers.changedCount})</summary>
              <table className="w-full text-xs border-collapse mt-2">
                <tbody>
                  {cmp.customers.changed.slice(0, 50).map((c) => (
                    <tr key={c.id} className="hover:bg-slate-700/30">
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-300">{c.name} <span className="text-slate-600">QBO {c.id}</span></td>
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-400">{money(c.before)} → {money(c.after)}</td>
                      <td className={`px-2 py-0.5 border-b border-slate-800/40 text-right font-mono ${c.delta > 0 ? 'text-green-400' : 'text-amber-300'}`}>{signed(c.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cmp.customers.changedCount > 50 && <div className="text-slate-500 mt-1">first 50 shown</div>}
            </details>
          )}

          {cmp.pnl.changed.length > 0 && (
            <details className="text-xs" open>
              <summary className="cursor-pointer text-red-300">P&amp;L cells that moved ({cmp.pnl.changedCount})</summary>
              <table className="w-full text-xs border-collapse mt-2">
                <tbody>
                  {cmp.pnl.changed.slice(0, 50).map((c, i) => (
                    <tr key={i} className="hover:bg-slate-700/30">
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-300">{c.row}</td>
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-400">{c.column}</td>
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-400">{money(c.before)} → {money(c.after)}</td>
                      <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-red-300">{signed(c.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          <div className="text-[11px] text-slate-500">{cmp.note}</div>
          {cmp.warnings.length > 0 && <div className="text-[11px] text-amber-300">{cmp.warnings.join(' · ')}</div>}
        </div>
      )}
    </div>
  );
}
